import React, { useState, useEffect, useRef } from 'react';
import { Room, RoomEvent, Track, VideoQuality } from 'livekit-client';
import axios from 'axios';
import { Phone, PhoneOff, Loader2 } from 'lucide-react';

// Create API instance without credentials for public embed access
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: false, // No credentials needed for public embed
  headers: {
    'Content-Type': 'application/json',
  },
});

const EmbedLiveAvatarPageContent = ({ agentId }) => {
  const [agent, setAgent] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [captions, setCaptions] = useState([]);
  const [room, setRoom] = useState(null);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [avatarVideoTrack, setAvatarVideoTrack] = useState(null);
  const [viewMode, setViewMode] = useState('full');
  const [dynamicVariableNames, setDynamicVariableNames] = useState([]);
  const [dynamicVariableValues, setDynamicVariableValues] = useState({});

  const audioRef = useRef(null);
  const videoRef = useRef(null);
  const fpsIntervalRef = useRef(null);
  const videoAttachedRef = useRef(false);
  const avatarReadyNotifiedRef = useRef(false);

  // Detect view mode from query string and extract dynamic variable names
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const mode = params.get('view');
    if (mode === 'dynamic') {
      setViewMode('dynamic');
      const systemParams = ['view', 'token', 'embed_token', 'key'];
      const variableNames = [];
      const initialValues = {};
      params.forEach((value, key) => {
        if (!systemParams.includes(key)) {
          variableNames.push(key);
          if (value && !value.startsWith('{{') && !value.endsWith('}}')) {
            initialValues[key] = value;
          }
        }
      });
      setDynamicVariableNames(variableNames);
      setDynamicVariableValues(initialValues);
    }
  }, []);

  useEffect(() => {
    // Component mounted
  }, [agentId]);

  // Load agent details
  useEffect(() => {
    const loadAgent = async () => {
      try {
        const response = await api.get(`/agents/${agentId}`);
        
        // Check if response is HTML instead of JSON (shouldn't happen in normal operation)
        if (typeof response.data === 'string' && response.data.trim().startsWith('<!DOCTYPE')) {
          setError('Received unexpected response format. Please check your API configuration.');
          setLoading(false);
          return;
        }
        
        setAgent(response.data);
        
        // Check if LiveAvatar is enabled - handle various formats
        const liveavatarEnabledRaw = response.data.liveavatar_enabled;
        const liveavatarEnabled = liveavatarEnabledRaw === true || 
                                 liveavatarEnabledRaw === 'true' || 
                                 liveavatarEnabledRaw === 1 ||
                                 liveavatarEnabledRaw === '1';
        const avatarId = response.data.liveavatar_avatar_id;
        const hasAvatarId = avatarId && typeof avatarId === 'string' && avatarId.trim() !== '';
        
        if (!liveavatarEnabled) {
          setError(`LiveAvatar is not enabled for this agent. Current value: ${liveavatarEnabledRaw} (type: ${typeof liveavatarEnabledRaw})`);
        } else if (!hasAvatarId) {
          setError(`Avatar ID is missing. Current value: ${avatarId} (type: ${typeof avatarId})`);
        }
      } catch (err) {
        setError(`Agent not found: ${err.response?.data?.detail || err.message}`);
      } finally {
        setLoading(false);
      }
    };

    if (agentId) {
      loadAgent();
    } else {
      setLoading(false);
    }
  }, [agentId]);

  const updateVariableValue = (name, value) => {
    if (!name) return;
    setDynamicVariableValues(prev => ({ ...prev, [name]: value }));
  };

  const getVariableLabel = (varName) => {
    if (!varName) return '';
    return varName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Start LiveAvatar session
  const startLiveAvatarSession = async () => {
    if (!agent) return;

    try {
      setIsConnecting(true);
      setError(null);

      // Generate unique room name for test
      const timestamp = Date.now();
      const roomName = `test-agent-${agent.id}-${timestamp}`;

      const variablesToSend = {};
      dynamicVariableNames.forEach(name => {
        const value = dynamicVariableValues[name];
        if (value && value.trim()) {
          variablesToSend[name] = value.trim();
        }
      });

      const sessionPayload = {
        room_name: roomName,
        agent_id: agent.id,
        channel: 'test',
        ...(Object.keys(variablesToSend).length > 0 && { variables: variablesToSend })
      };

      // Create test session
      const sessionResponse = await api.post('/sessions/test', sessionPayload);
      const testSessionId = sessionResponse.data.session_id;
      setSessionId(testSessionId);
      
      // Get LiveKit token
      const tokenResponse = await api.post('/token', {
        room: roomName,
        identity: 'liveavatar-user'
      });
      
      const { url, token } = tokenResponse.data;
      
      // Create and connect to LiveKit room
      const newRoom = new Room({
        adaptiveStream: true,
        dynacast: true,
        disconnectOnPageLeave: false, // Keep call active when user switches browser tabs
        videoCaptureDefaults: {
          resolution: { width: 640, height: 360 }
        },
        // Enable video subscription for remote participants
        defaultAudioCaptureOptions: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      const logAvatarVideoResolution = (publication, track, label = 'Avatar video') => {
        const fromPub = publication?.dimensions ? `${publication.dimensions.width}x${publication.dimensions.height}` : null;
        const fromTrack = track?.dimensions ? `${track.dimensions.width}x${track.dimensions.height}` : null;
        const q = publication?.videoQuality;
        const reqLabel = q === VideoQuality.LOW ? 'LOW (360p)' : q === VideoQuality.MEDIUM ? 'MEDIUM' : q === VideoQuality.HIGH ? 'HIGH (720p)' : 'unknown';
        console.log(`[LiveAvatar] ${label} — requested: ${reqLabel} | FPS: 30 | publication: ${fromPub || 'n/a'} | track: ${fromTrack || 'n/a'}`);
        const logPlayback = () => {
          const el = videoRef.current;
          const w = el?.videoWidth ?? 0;
          const h = el?.videoHeight ?? 0;
          if (w && h) {
            const is720 = w >= 1280 || h >= 720;
            const is360 = w <= 640 && h <= 360;
            console.log(`[LiveAvatar] ${label} — actual playback: ${w}x${h} → ${is720 ? '720p' : is360 ? '360p' : 'other'}`);
            if (typeof el.requestVideoFrameCallback === 'function') {
              if (fpsIntervalRef.current) {
                clearInterval(fpsIntervalRef.current);
                fpsIntervalRef.current = null;
              }
              const measureAndLogFps = () => {
                const videoEl = videoRef.current;
                if (!videoEl?.videoWidth || typeof videoEl.requestVideoFrameCallback !== 'function') return;
                let frameCount = 0;
                const start = performance.now();
                const countFrame = () => {
                  frameCount++;
                  if (performance.now() - start < 1100) {
                    videoEl.requestVideoFrameCallback(countFrame);
                  } else {
                    const fps = Math.round((frameCount * 1000) / (performance.now() - start));
                    console.log(`[LiveAvatar] ${label} — FPS: ~${fps}`);
                  }
                };
                videoEl.requestVideoFrameCallback(countFrame);
              };
              measureAndLogFps();
              fpsIntervalRef.current = setInterval(measureAndLogFps, 2000);
            } else {
              console.log(`[LiveAvatar] ${label} — actual FPS: (not measurable in this browser)`);
            }
          } else {
            console.log(`[LiveAvatar] ${label} — actual playback: ${w}x${h} (video not ready yet; may need user interaction to play)`);
          }
        };
        setTimeout(logPlayback, 800);
        setTimeout(logPlayback, 2500);
      };

      const attachVideoTrack = (track, publication) => {
        if (!track || !videoRef.current) return false;
        if (videoAttachedRef.current) return true;
        
        try {
          track.attach(videoRef.current);
          
          if (!videoRef.current.srcObject && track.mediaStreamTrack) {
            try {
              videoRef.current.srcObject = new MediaStream([track.mediaStreamTrack]);
            } catch {}
          }
          
          videoAttachedRef.current = true;
          
          setTimeout(() => {
            if (videoRef.current?.srcObject) {
              videoRef.current.play().catch(() => {});
            }
          }, 300);
          
          return true;
        } catch (error) {
          return false;
        }
      };
      
      // Helper function to check if participant is avatar and subscribe to video
      const subscribeToAvatarVideo = (participant) => {
        const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                        participant.name === 'liveavatar-avatar-agent' ||
                        (participant.identity && participant.identity.includes('liveavatar')) ||
                        (participant.name && participant.name.includes('liveavatar'));
        
        if (isAvatar) {
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track) {
              try {
                publication.setVideoQuality(VideoQuality.LOW);
                publication.setVideoFPS(30);
              } catch (_) {}
              setAvatarVideoTrack(publication.track);
              attachVideoTrack(publication.track, publication);
              logAvatarVideoResolution(publication, publication.track, 'Avatar video (existing)');
            }
          });
        }
      };
      
      // Set up event listeners
      newRoom.on(RoomEvent.Connected, () => {
        setIsConnected(true);
        setIsConnecting(false);
        addCaption('system', '✅ Connected to LiveAvatar agent');
        
        // Check all existing participants for avatar
        newRoom.remoteParticipants.forEach((participant) => {
          subscribeToAvatarVideo(participant);
        });
      });
      
      newRoom.on(RoomEvent.Disconnected, () => {
        if (fpsIntervalRef.current) {
          clearInterval(fpsIntervalRef.current);
          fpsIntervalRef.current = null;
        }
        videoAttachedRef.current = false;
        avatarReadyNotifiedRef.current = false;
        setIsConnected(false);
        setAvatarVideoTrack(null);
        addCaption('system', '❌ Disconnected from LiveAvatar agent');
      });
      
      // Listen for new participants joining
      newRoom.on(RoomEvent.ParticipantConnected, (participant) => {
        subscribeToAvatarVideo(participant);
      });
      
      // Listen for track subscriptions
      newRoom.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
        if (track.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            try {
              publication.setVideoQuality(VideoQuality.LOW);
              publication.setVideoFPS(30);
            } catch (_) {}
            setAvatarVideoTrack(track);
            
            // Attach video track to video element
            if (!attachVideoTrack(track, publication)) {
              // Retry attachment after a short delay if video ref not ready
              setTimeout(() => {
                if (videoRef.current && track) {
                  attachVideoTrack(track, publication);
                }
              }, 500);
            }
            logAvatarVideoResolution(publication, track, 'Avatar video (TrackSubscribed)');
          }
        } else if (track.kind === 'audio') {
          // Play remote audio from any participant
          if (audioRef.current) {
            track.attach(audioRef.current);
          }
        }
      });
      
      // Listen for track publications (when tracks are published but not yet subscribed)
      newRoom.on(RoomEvent.TrackPublished, (publication, participant) => {
        if (publication.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            // LiveKit will auto-subscribe - TrackSubscribed event will fire automatically
            // No manual subscription needed
          }
        }
      });
      
      newRoom.on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
        if (track.kind === 'video') {
          const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                          participant.name === 'liveavatar-avatar-agent' ||
                          (participant.identity && participant.identity.includes('liveavatar')) ||
                          (participant.name && participant.name.includes('liveavatar'));
          
          if (isAvatar) {
            videoAttachedRef.current = false;
            avatarReadyNotifiedRef.current = false;
            setAvatarVideoTrack(null);
            if (videoRef.current) {
              track.detach(videoRef.current);
            }
          }
        }
      });
      
      newRoom.on(RoomEvent.DataReceived, (payload, participant) => {
        try {
          const decoder = new TextDecoder();
          const data = JSON.parse(decoder.decode(payload));
          
          // Handle transcription data
          if (data.type === 'transcription') {
            addCaption(data.speaker || 'agent', data.text);
          }
          
          // Handle conversation items
          if (data.type === 'conversation_item') {
            addCaption(data.speaker || 'agent', data.text);
          }
        } catch (e) {
          // Silently handle parsing errors
        }
      });
      
      // Connect to room
      await newRoom.connect(url, token);
      setRoom(newRoom);
      
      // Enable microphone
      await newRoom.localParticipant.setMicrophoneEnabled(true);
      
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to start call';
      setError(detail);
      setIsConnecting(false);
    }
  };

  // Stop LiveAvatar session
  const stopLiveAvatarSession = async () => {
    try {
      if (room) {
        await room.disconnect();
        setRoom(null);
      }
      
      if (sessionId) {
        await api.post(`/sessions/${sessionId}/end`);
      }
      
      videoAttachedRef.current = false;
      avatarReadyNotifiedRef.current = false;
      setIsConnected(false);
      setSessionId(null);
      setCaptions([]);
      setAvatarVideoTrack(null);
      
    } catch (err) {
      // Silently handle stop errors
    }
  };

  // Add caption to transcript
  const addCaption = (speaker, text) => {
    if (!text || !text.trim()) return;
    
    setCaptions(prev => {
      // Deduplicate
      const lastCaption = prev[prev.length - 1];
      if (lastCaption && lastCaption.speaker === speaker && lastCaption.text === text) {
        return prev;
      }
      
      return [...prev, {
        speaker,
        text,
        timestamp: new Date().toLocaleTimeString()
      }];
    });
  };

  // Fallback poll for avatar video (events are primary, this catches edge cases)
  useEffect(() => {
    if (!room || !isConnected) return;
    
    const checkForAvatarVideo = () => {
      if (videoAttachedRef.current) return;
      
      room.remoteParticipants.forEach((participant) => {
        const isAvatar = participant.identity === 'liveavatar-avatar-agent' || 
                        participant.name === 'liveavatar-avatar-agent' ||
                        (participant.identity && participant.identity.includes('liveavatar')) ||
                        (participant.name && participant.name.includes('liveavatar'));
        
        if (isAvatar && !videoAttachedRef.current) {
          participant.videoTrackPublications.forEach((publication) => {
            if (publication.track && !videoAttachedRef.current) {
              try {
                publication.setVideoQuality(VideoQuality.LOW);
                publication.setVideoFPS(30);
              } catch (_) {}
              setAvatarVideoTrack(publication.track);
              if (videoRef.current) {
                try {
                  publication.track.attach(videoRef.current);
                  if (!videoRef.current.srcObject && publication.track.mediaStreamTrack) {
                    videoRef.current.srcObject = new MediaStream([publication.track.mediaStreamTrack]);
                  }
                  videoAttachedRef.current = true;
                  videoRef.current.play().catch(() => {});
                } catch {}
              }
            }
          });
        }
      });
    };
    
    checkForAvatarVideo();
    const interval = setInterval(checkForAvatarVideo, 500);
    return () => clearInterval(interval);
  }, [room, isConnected]);
  
  // Ensure video plays when track is attached
  useEffect(() => {
    if (avatarVideoTrack && videoRef.current) {
      // Small delay to ensure track is attached
      const timeout = setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play().catch(() => {
            // Silently handle play errors
          });
        }
      }, 200);
      
      return () => clearTimeout(timeout);
    }
  }, [avatarVideoTrack]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (fpsIntervalRef.current) {
        clearInterval(fpsIntervalRef.current);
        fpsIntervalRef.current = null;
      }
      if (room) {
        room.disconnect();
      }
    };
  }, [room]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading LiveAvatar agent...</p>
        </div>
      </div>
    );
  }

  if (error && !agent) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center">
        <div className="text-center px-4">
          <p className="text-destructive text-lg mb-2">{error}</p>
          <p className="text-muted-foreground">Please check the agent configuration.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 via-gray-900 to-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-4xl">
        <div className="bg-gray-900/50 backdrop-blur-sm border border-gray-800 rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary/20 to-primary/10 border-b border-gray-800 p-6">
            <h1 className="text-2xl font-bold text-white mb-2">BrainCX LiveAvatar</h1>
            <p className="text-gray-400 text-sm">{agent?.name || 'Voice AI Agent'}</p>
          </div>

          {/* Avatar Video Container */}
          <div className="relative bg-black aspect-video flex items-center justify-center">
            {avatarVideoTrack ? (
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted={false}
                className="w-full h-full object-contain"
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    videoRef.current.play().catch(() => {});
                  }
                }}
                onCanPlay={() => {
                  if (videoRef.current) {
                    videoRef.current.play().catch(() => {});
                  }
                  if (!avatarReadyNotifiedRef.current && room?.localParticipant) {
                    avatarReadyNotifiedRef.current = true;
                    try {
                      const msg = new TextEncoder().encode(JSON.stringify({ type: 'avatar_video_ready' }));
                      room.localParticipant.publishData(msg, { reliable: true, topic: 'avatar_status' });
                    } catch {}
                  }
                }}
                onError={() => {}}
              />
            ) : (
              <div className="text-center">
                {isConnected ? (
                  <>
                    <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
                    <p className="text-gray-400">Waiting for avatar to appear...</p>
                  </>
                ) : (
                  <>
                    <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gray-800 flex items-center justify-center">
                      <Phone className="w-12 h-12 text-gray-600" />
                    </div>
                    <p className="text-gray-400">Click Start to begin conversation</p>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Dynamic Variable Input Fields */}
          {viewMode === 'dynamic' && dynamicVariableNames.length > 0 && (
            <div className="px-6 py-4 border-t border-gray-800 bg-gray-900/30">
              <h3 className="text-sm font-semibold text-gray-300 mb-3">Your Information</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {dynamicVariableNames.map((varName) => (
                  <div key={varName} className="space-y-1">
                    <label className="text-xs font-medium text-gray-400 block">{getVariableLabel(varName)}</label>
                    <input
                      type="text"
                      value={dynamicVariableValues[varName] || ''}
                      onChange={(e) => updateVariableValue(varName, e.target.value)}
                      placeholder={`Enter ${getVariableLabel(varName).toLowerCase()}`}
                      disabled={isConnected}
                      className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent disabled:opacity-60 disabled:cursor-not-allowed"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Controls */}
          <div className="p-6 border-t border-gray-800">
            <div className="flex gap-4 mb-6">
              {!isConnected ? (
                <button
                  onClick={startLiveAvatarSession}
                  disabled={isConnecting || !agent?.liveavatar_enabled}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isConnecting ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      Connecting...
                    </>
                  ) : (
                    <>
                      <Phone className="w-5 h-5" />
                      Start Conversation
                    </>
                  )}
                </button>
              ) : (
                <button
                  onClick={stopLiveAvatarSession}
                  className="flex-1 px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg font-medium transition-all duration-300 flex items-center justify-center gap-2"
                >
                  <PhoneOff className="w-5 h-5" />
                  End Conversation
                </button>
              )}
            </div>

            {/* Transcript */}
            {captions.length > 0 && (
              <div className="bg-gray-900/50 rounded-lg p-4 max-h-48 overflow-y-auto">
                <h3 className="text-sm font-semibold text-gray-300 mb-3">Conversation</h3>
                <div className="space-y-2">
                  {captions.map((caption, index) => (
                    <div key={index} className="text-sm">
                      <span className="text-gray-400 font-medium">
                        {caption.speaker === 'user' ? 'You' : 'Agent'}:
                      </span>
                      <span className="text-gray-300 ml-2">{caption.text}</span>
                      <span className="text-gray-500 text-xs ml-2">{caption.timestamp}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-900/20 border border-red-800 rounded-lg">
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Hidden audio element for remote audio */}
      <audio ref={audioRef} autoPlay />
    </div>
  );
};

const EmbedLiveAvatarPage = ({ agentId }) => {
  return <EmbedLiveAvatarPageContent agentId={agentId} />;
};

export default EmbedLiveAvatarPage;

